<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    width="420"
    class="add-member-drawer"
    @input="$emit('input', $event)"
  >
    <div class="add-member-drawer__container">
      <div class="add-member-drawer__header">
        <div class="add-member-drawer__title">
          {{ $t("broker.members.addMemberTitle") }}
        </div>

        <button icon @click="$emit('input', false)">
          <v-icon>fa-regular fa-xmark</v-icon>
        </button>
      </div>

      <v-divider />

      <div class="add-member-drawer__body">
        <div class="add-member-drawer__sectionTitle">
          {{ $t("broker.members.membersInfo") }}
        </div>

        <div class="add-member-drawer__formGrid">
          <div>
            <div class="add-member-drawer__fieldLabel">
              {{ $t("broker.members.memberEmail") }}
            </div>
            <v-text-field
              v-model="form.email"
              dense
              outlined
              :rules="emailRules"
              :error-messages="emailError"
              :validate-on-blur="true"
              :placeholder="$t('broker.members.enterMemberEmail')"
            />
          </div>

          <div>
            <div class="add-member-drawer__fieldLabel">
              {{ $t("broker.members.roleName") }}
            </div>
            <div class="add-member-drawer__roleRow">
              <v-select
                v-model="form.role"
                :items="roleOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                :placeholder="$t('broker.members.selectRole')"
                class="add-member-drawer__roleSelect"
              />

              <v-btn
                class="add-member-drawer__miniAddBtn"
                depressed
                :disabled="!canAddRow"
                @click="addRow"
              >
                <v-icon size="18">fa-regular fa-user-plus</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <div v-if="rows.length" class="add-member-drawer__rowsPreview">
          <div
            v-for="(r, idx) in rows"
            :key="idx"
            class="add-member-drawer__rowItem"
          >
            <div class="add-member-drawer__left">
              <v-icon size="16">fa-regular fa-user</v-icon>
              <div class="add-member-drawer__rowEmail">{{ r.email }}</div>
            </div>

            <div class="add-member-drawer__center">
              <div
                class="add-member-drawer__roleChip"
                :class="`add-member-drawer__roleChip--${r.role?.toLowerCase()}`"
              >
                {{ roleLabel(r.role) }}
              </div>
            </div>
            <button  class="add-member-drawer__trashBtn" icon @click="removeRow(idx)">
              <v-icon size="16"
                >fa-regular fa-trash</v-icon
              >
            </button>
            
          </div>
        </div>
      </div>

      <div class="add-member-drawer__footer">
        <v-btn class="add-member-drawer__cancelBtn" depressed @click="cancel">
          {{ $t("broker.members.cancel") }}
        </v-btn>

        <v-btn
          class="add-member-drawer__inviteBtn"
          depressed
          :disabled="rows.length === 0"
          @click="submit"
        >
          {{ $t("broker.members.inviteMembers") }}
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "MemberDrawer",
  props: {
    value: { type: Boolean, default: false }, // v-model
    roleOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: { email: "", role: null },
      rows: [],
      emailRules: [
        (v) => !!v || this.$t("broker.members.emailRequired"),
        (v) =>
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
          this.$t("broker.members.invalidEmail"),
      ],
      emailError: "",
    };
  },
  computed: {
    canAddRow() {
      const email = (this.form.email || "").trim();
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      return isValidEmail && !!this.form.role;
    },
  },
  watch: {
    // cuando se cierre desde afuera, limpiamos (opcional)
    value(v) {
      if (!v) this.reset();
    },
    "form.email"() {
      this.emailError = "";
    },
  },
  methods: {
    roleLabel(role) {
      return role === "ADMIN" ? "Admin" : "Read-Only";
    },
    addRow() {
      if (!this.canAddRow) return;

      const email = (this.form.email || "").trim().toLowerCase();

      const exists = this.rows.some((r) => r.email.toLowerCase() === email);

      if (exists) {
        this.emailError = "This email was already added";
        return;
      }
      this.emailError = "";

      this.rows.push({
        email,
        role: this.form.role,
      });

      this.form.email = "";
      this.form.role = null;
    },
    submit() {
      const payload = this.rows.map((r) => ({ email: r.email, role: r.role }));
      this.$emit("invite", payload);
      this.reset();
    },
    reset() {
      this.form = { email: "", role: null };
      this.rows = [];
    },
    removeRow(idx) {
      if (idx == null || idx < 0 || idx >= this.rows.length) return;
      this.rows.splice(idx, 1);
      if (this.emailError) this.emailError = "";
    },
  },
};
</script>

<style lang="scss" scoped>
.add-member-drawer {
  height: 100%;

  &__container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  // Header
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 18px 12px;
  }

  &__title {
    font-size: 18px;
    font-weight: 800;
    line-height: 1.2;
  }

  &__body {
    padding: 14px 18px;
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  // body - __formGrid
  &__sectionTitle {
    font-size: 12px;
    font-weight: 800;
    margin-bottom: 10px;
    opacity: 0.85;
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  &__fieldLabel {
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 6px;
    opacity: 0.8;
  }

  &__roleRow {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__roleSelect {
    flex: 1;
  }

  &__miniAddBtn {
    border-radius: 8px;
    min-width: 44px;
    height: 40px;
  }

  //__rowsPreview
  &__rowsPreview {
    margin-top: 18px;
  }

  &__rowsTitle {
    font-size: 12px;
    font-weight: 800;
    margin-bottom: 10px;
    opacity: 0.85;
  }

  &__rowItem {
    display: grid;
    grid-template-columns: 2fr 1fr auto; // left | center | trash
    align-items: center;
    gap: 12px;
    padding: 10px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 10px;
    margin-bottom: 10px;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__right {
    display: flex;
    width: 130px;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  &__rowEmail {
    font-size: 13px;
    font-weight: 700;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

    &__center {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  // footer- buttons
  &__footer {
    margin-top: auto;
    padding: 12px 18px 16px;
    display: flex;
    gap: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  &__cancelBtn {
    flex: 1;
    border-radius: 10px;
    text-transform: none;
    background: rgba(0, 0, 0, 0.06);
  }

  &__inviteBtn {
    flex: 1;
    border-radius: 10px;
    text-transform: none;
    background: rgba(32, 227, 162, 0.35);
  }

  &__roleChip {
     width: fit-content;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    margin-right: 0;
    min-width: 0;
    border-radius: 999px;
    font-weight: 700;
    font-size: 12px;
    white-space: nowrap;
  }

  &__trashBtn {
    justify-self: end;
  }

  &__roleChip--read_only,
  &__roleChip--readonly {
    background: #d7ffed;
    color: #005931 !important;
  }

  &__roleChip--admin {
    background: #e0f3fe;
    color: #045684 !important;
  }
}

.broker-app
  .v-application
  .add-member-drawer__cancelBtn.v-btn:not(.v-btn--disabled) {
  background-color: #ebebeb !important;
  color: #14161b !important;
}

.broker-app .v-application.theme--light .add-member-drawer__rowEmail {
  color: #3b3b3b !important;
}

::v-deep .v-text-field.v-text-field--enclosed .v-text-field__details {
  padding: 0px !important;
}

::v-deep .v-application .v-messages__message {
  margin-bottom: 0px !important;
}
</style>
